/**
 * MobileSupervisionsRecordsList — record cards ( <lg ). Batch 6.4.
 * Uses same GET /supervisions the desktop page loads. Card tap opens
 * shared SupervisionForm dialog via `onOpen(record)`. Lock icon read
 * verbatim from server `read_only`. Care worker's list is already
 * narrowed by the visibility hardening — mobile does no client filter.
 */
import React from "react";
import { ChevronRight, Lock, Pencil, User, Calendar, AlertOctagon, Clock, CheckCircle2, CalendarClock } from "lucide-react";

const STATUS_META = {
  draft:       { label: "Draft",       Icon: Clock,        cls: "bg-slate-100 text-slate-700 border-slate-300" },
  scheduled:   { label: "Scheduled",   Icon: Clock,        cls: "bg-sky-100 text-sky-800 border-sky-300" },
  in_progress: { label: "In Progress", Icon: Clock,        cls: "bg-amber-100 text-amber-900 border-amber-300" },
  completed:   { label: "Completed",   Icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  cancelled:   { label: "Cancelled",   Icon: AlertOctagon, cls: "bg-red-100 text-red-800 border-red-300" },
};

const DUE_TONES = {
  overdue:  "bg-red-100 text-red-800 border-red-300",
  due_soon: "bg-amber-100 text-amber-900 border-amber-400",
  on_track: "bg-emerald-100 text-emerald-800 border-emerald-300",
  unknown:  "bg-slate-100 text-slate-600 border-slate-300",
};

export default function MobileSupervisionsRecordsList({ records, onOpen }) {
  if (!records || records.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center" data-testid="mobile-sup-empty">
        <div className="text-sm font-semibold text-slate-700">No supervisions to show.</div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="mobile-sup-records">
      <div className="text-xs text-slate-500 tabular-nums" data-testid="mobile-sup-count">
        {records.length} supervision{records.length === 1 ? "" : "s"}
      </div>
      <ul className="space-y-2" data-testid="mobile-sup-list">
        {records.map((r) => {
          const meta = STATUS_META[r.status] || STATUS_META.draft;
          const Icon = meta.Icon;
          const nextTone = r.next_due_date
            ? (new Date(r.next_due_date) < new Date() ? DUE_TONES.overdue : DUE_TONES.on_track)
            : DUE_TONES.unknown;
          return (
            <li key={r.id} data-testid={`mobile-sup-row-${r.id}`} className="rounded-lg border border-slate-200 bg-white">
              <button type="button" onClick={() => onOpen?.(r)}
                data-testid={`mobile-sup-card-${r.id}`}
                className="w-full text-left p-3 min-h-[44px] active:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-cyan-500 rounded-lg">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <div className="font-semibold text-slate-900 truncate" data-testid={`mobile-sup-staff-${r.id}`}>
                        {r.staff_name || "—"}
                      </div>
                      {r.read_only && <Lock className="w-3.5 h-3.5 text-slate-500 shrink-0" data-testid={`mobile-sup-lock-${r.id}`} />}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 capitalize">
                      {(r.supervision_type || "supervision").replace(/-/g, " ")}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 mt-1" />
                </div>
                <div className="text-[11px] text-slate-600 mt-1 flex items-center gap-2 flex-wrap tabular-nums">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3 text-slate-400" /> Held {r.held_date || "—"}</span>
                  <span className="text-slate-400">·</span>
                  <span className="flex items-center gap-1"><User className="w-3 h-3 text-slate-400" /> {r.supervisor_name || r.conducted_by || "—"}</span>
                </div>
                <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                  <span data-testid={`mobile-sup-status-${r.id}`} data-status={r.status}
                    className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${meta.cls}`}>
                    <Icon className="w-3 h-3" /> {meta.label}
                  </span>
                  <span data-testid={`mobile-sup-next-${r.id}`}
                    className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${nextTone}`}>
                    <CalendarClock className="w-3 h-3" /> Next {r.next_due_date || "—"}
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-cyan-700">
                    {r.read_only ? <><Lock className="w-3 h-3" /> View</> : <><Pencil className="w-3 h-3" /> Open</>}
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
